import './widget.css';

import {
  IonButton,
  IonIcon,
  IonInput,
  IonLabel,
  IonPopover,
  IonRange,
  IonRippleEffect,
  isPlatform,
} from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import { addOutline, removeOutline } from 'ionicons/icons';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { PopoverState } from '../../../../../components/ui/ui-interfaces';
import { useKeyboardIsShowing } from '../../../../../theme/keyboard.hook';
import { useIsMounted } from '../../../../../util/hooks';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { formatNumber } from '../../util/math';
import { getNumericFormatSuffix } from '../../util/widget';
import { WidgetLabel } from './widget-label';
import {
  NumericFormat,
  SliderWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

interface SliderInputProps extends SliderWidgetProps {
  onChange?: (val: number) => void;
  showIncrementIcons?: boolean;
  format?: NumericFormat;
}

const SliderInput = (props: SliderInputProps & FieldConfig): ReactElement => {
  const {
    label,
    description,
    showDescription,
    max,
    min,
    step,
    ionItemProps,
    extensions,
    disabled,
    subtitle,
    subtitleColor,
    ticks,
    snaps,
    onChange,
    showIncrementIcons,
    format,
    isNested,
    isPaywalled,
    transparentBackground,
    ...otherProps
  } = props;

  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);

  const { value: fieldValue } = field;

  const [tempValue, setTempValue] = useState<number>(0);
  useEffect(() => {
    setTempValue(fieldValue ?? min);
  }, [fieldValue, min]);
  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  } as PopoverState);

  const inputRef = useRef<HTMLIonInputElement>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);
  const isKeyboardShowing = useKeyboardIsShowing();

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    includeClasses: ['slider-widget'],
    isNested,
    isRippleUtilized: true,
    transparentBackground,
    isPaywalled,
  });

  const ionLabelClass = isNested ? 'ion-padding-start' : undefined;

  const isMounted = useIsMounted();

  const showOptions = (
    e: React.MouseEvent<HTMLIonInputElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();
    if (isMounted()) {
      setShowPopover({ showPopover: true, event: e as unknown as Event });
    }
  };

  const hideOptions = () => {
    if (isMounted()) {
      setShowPopover({ showPopover: false, event: undefined });
    }
  };

  const onChangeInteral = useCallback(
    (val: number) => {
      helper.setValue(val);

      if (typeof onChange === 'function') {
        onChange(val);
      }
    },
    [helper, onChange]
  );

  const isIOS = isPlatform('ios');

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      disabled={isDisabled}
      onClick={async () => {
        if (!isKeyboardShowing) {
          const nativeInput = await inputRef.current?.getInputElement();
          nativeInput?.click();
        }
      }}
      button={rightElement ? false : !isIOS}
      {...restIonItemProps}
    >
      <IonInput
        aria-label={label}
        data-cy={otherProps.name}
        ref={inputRef}
        readonly={true}
        disabled={isDisabled}
        value={field.value}
        onTouchStart={() => {
          if (isKeyboardShowing) {
            const el = document.activeElement as HTMLInputElement;
            if (typeof el?.blur === 'function') {
              el?.blur();
            }
          }
        }}
        onClick={(e) => {
          if (isPaywalled) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          e.persist();
          showOptions(e);
        }}
      >
        <div slot={'label'}>
          <WidgetLabel
            label={label}
            description={description}
            error={meta.error}
            forceDisable={isDisabled}
            subtitle={subtitle}
            subtitleColor={subtitleColor}
            ionLabelProps={{
              className: ionLabelClass,
            }}
          />
        </div>
        {rightElement}
      </IonInput>
      {format && getNumericFormatSuffix(format)}
      <IonPopover
        data-cy="slider-popover"
        event={popoverState.event}
        isOpen={popoverState.showPopover}
        onDidDismiss={() => {
          hideOptions();
        }}
        showBackdrop={true}
      >
        <div className="widget-slider-popover">
          <div className="slider-label-container">
            <IonLabel>{label}</IonLabel>
            <IonLabel>{formatNumber(tempValue, step)}</IonLabel>
          </div>
          <IonRange
            aria-labelledby="input-slider"
            data-cy="widget-slider"
            pin={true}
            value={tempValue}
            max={max}
            min={min}
            step={step}
            ticks={snaps}
            snaps={true}
            activeBarStart={min}
            pinFormatter={(val) => {
              return formatNumber(val, step);
            }}
            onIonChange={(e) => {
              if (isPaywalled) {
                return;
              }

              const val = formatNumber(Number(e.detail.value.valueOf()), step);
              onChangeInteral(val);
            }}
          >
            {showIncrementIcons && (
              <>
                <IonButton
                  slot="start"
                  size="small"
                  fill="clear"
                  onClick={() => {
                    onChangeInteral(field.value - 1);
                  }}
                >
                  <IonIcon icon={removeOutline} />
                </IonButton>
                <IonButton
                  slot="end"
                  size="small"
                  fill="clear"
                  onClick={() => {
                    onChangeInteral(field.value + 1);
                  }}
                >
                  <IonIcon icon={addOutline} />
                </IonButton>
              </>
            )}
          </IonRange>
        </div>
      </IonPopover>
      {!isIOS && <IonRippleEffect />}
    </IonItem>
  );
};

export { SliderInput };
